// src/components/home/VibeHero.jsx - Compact version with reduced dead space
import React from 'react';
import { motion } from 'framer-motion';
import { Brain, ArrowRight, Sparkles, Star, Zap, Crown } from 'lucide-react';
import Button from '../shared/Button';
import { useApp } from '../../context/AppContext';

const VibeHero = () => {
  const { setCurrentView } = useApp();
  
  return (
   <section className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-32 pb-16 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-5xl mx-auto space-y-8"
        >
          {/* Floating Elements */}
          <div className="relative">
            <motion.div
              animate={{ y: [-10, 10, -10] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -top-8 -left-8 text-5xl"
            >
              ✨
            </motion.div>
            <motion.div
              animate={{ y: [10, -10, 10] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -top-4 -right-12 text-5xl"
            >
              🧠
            </motion.div>
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity }}
              className="absolute top-12 left-12 text-2xl"
            >
              💫
            </motion.div>
          </div>

          {/* Main Hero Content */}
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Crown className="w-6 h-6 text-yellow-400" />
              <span className="px-3 py-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-full text-purple-300 font-medium">
                Vibe Edition
              </span>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
              Transform Your
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400 animate-gradient-shift">
                Inner Voice
              </span>
            </h1>
            
            <p className="text-lg md:text-xl lg:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Master your self-talk with science-backed techniques designed for young adults. 
              <span className="text-purple-300 font-semibold"> No boring lectures, just real transformation.</span>
            </p>
          </motion.div>

          {/* Vibe Points */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="flex flex-wrap justify-center gap-4 text-sm"
          >
            {[
              { icon: Zap, text: '8-minute micro-lessons', color: 'text-yellow-400' },
              { icon: Star, text: 'Gamified progress', color: 'text-purple-400' },
              { icon: Brain, text: '100% privacy-first', color: 'text-green-400' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10"
              >
                <item.icon className={`w-4 h-4 ${item.color}`} />
                <span className="text-white font-medium">{item.text}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button 
              size="lg" 
              className="min-w-[220px] bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-xl text-lg font-semibold"
              onClick={() => setCurrentView('vibe-assessment-intro')}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Start Your Vibe Check
            </Button>
            <Button 
              variant="secondary" 
              size="lg"
              className="min-w-[200px] text-lg"
              onClick={() => setCurrentView('modules')}
            >
              Explore Modules
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="text-center"
          >
            <p className="text-slate-400 text-sm mb-4">
              Free to start • No email required • Data stays on your device
            </p>
            <div className="flex justify-center items-center gap-8 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                Science-backed
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                Designed to wow
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                Privacy-first
              </span>
            </div>
          </motion.div>

          {/* Compact Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="pt-4"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-slate-400"
            >
              <div className="w-5 h-8 border-2 border-slate-600 rounded-full mx-auto relative">
                <motion.div
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-1 h-2 bg-purple-400 rounded-full absolute left-1/2 top-1 transform -translate-x-1/2"
                />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default VibeHero;